import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {useNavigate} from "react-router-dom";
import {ServiceFormLayout} from "../../createService/service-form-layout";
import {
  editBundleBasicFormStepFormValidationSchema,
  EditBundleBasicFormStepFormValues,
} from "../schema-validation";
import {EditBundleFormValues} from "../types";
import {EditBundleSessionOfferingForm} from "../../../../components/offering-form/session-bundle/bundle";
import {useEditBundleHook} from "../hooks/edit-bundle";
import {useGetBundleContext} from "../context/get-bundle";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {useDeleteBundleHook} from "../hooks/delete-bundle";
import {useEffect, useMemo} from "react";
import {
  useCoverImagesWithFallback,
  mapDomainToEmbedded,
} from "../../../../features/media";
import {useOAuthCalendarResume} from "../../../../utils/google-calendar-resume";
import {useOfferingSchedules} from "@/features/availability";
import {scrollToFirstOfferingFormError} from "../../../../components/offering-form/accordion/scroll-to-first-error";
import {toSavedEditBundleFormValues} from "../saved-form-values";
import {IS_PWYW_ENABLED} from "../../../../services/config";

export function EditBundleBasicForm() {
  const navigate = useNavigate();
  const {
    state: {sessionData},
  } = useGetBundleContext();
  const {editBundle, isEditingBundle} = useEditBundleHook();
  const {deleteBundle} = useDeleteBundleHook(sessionData.id);
  const snapshot = useOAuthCalendarResume<EditBundleBasicFormStepFormValues>();
  const {preferredSchedule} = useOfferingSchedules();

  // Fetch cover images via v2 with fallback to embedded data
  const {coverImages} = useCoverImagesWithFallback({
    offeringType: "service", // bundles are service type
    offeringId: sessionData.id,
    embedded: sessionData.cover_images,
  });

  // Convert domain format to API format for form state (memoized to prevent infinite loops)
  const coverImagesForForm = useMemo(
    () => mapDomainToEmbedded(coverImages),
    [coverImages]
  );

  const formMethods = useForm<EditBundleBasicFormStepFormValues>({
    resolver: yupResolver(editBundleBasicFormStepFormValidationSchema) as any,
    mode: "onSubmit",
    defaultValues: snapshot ?? {
      ...toSavedEditBundleFormValues(sessionData, IS_PWYW_ENABLED, preferredSchedule?.id),
      cover_images: sessionData.cover_images,
    },
  });
  // console.log({editformData: sessionData});

  // Sync cover images from v2 fetch to form state for validation
  useEffect(() => {
    formMethods.setValue("cover_images", coverImagesForForm);
    if (coverImagesForForm.length > 0) {
      formMethods.clearErrors("cover_images");
    } else {
      formMethods.trigger("cover_images");
    }
  }, [formMethods, coverImagesForForm]);

  function onSubmit(values: EditBundleBasicFormStepFormValues) {
    // console.log({values});
    const payload: EditBundleFormValues = {
      bookingQuestions: sessionData.bookingQuestions
        ? sessionData.bookingQuestions.map((q) => {
            if (
              q.answer_type === "checkbox" ||
              q.answer_type === "dropdown" ||
              q.answer_type === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: q.options.map((o) => ({
                  optionLabel: o,
                })),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: [
                  {
                    optionLabel: "",
                  },
                ],
              };
            }
          })
        : [],
      callDuration: values.callDuration,
      callLocation: values.callLocation,
      callPrice: values.callPrice,
      callPriceCurrency: values.callPriceCurrency,
      callPriceType: values.callPriceType,
      coverImage: values.coverImage,
      currencies:
        values.callPriceType === "free"
          ? []
          : [
              {
                callPrice: Number(values.callPrice),
                callPriceCurrency: values.callPriceCurrency,
              },
              ...values.currencies,
            ],
      customCallDuration: values.customCallDuration,
      description: values.description,
      hideFromProfile: values.hideFromProfile,
      id: sessionData.id,
      isPaused: values.isPaused,
      isUrlRedirect: values.isUrlRedirect,
      name: values.name,
      requestCustomerPhoneNumber: values.requestCustomerPhoneNumber,
      slug: values.slug,
      url_redirect: values.url_redirect,
      redirectAfterPurchase: values.redirectAfterPurchase,
      telegramGroupId: values.telegramGroupId,
      bookingInterval: values.bookingInterval,
      numberOfSessions: values.numberOfSessions,
      cover_images: values.cover_images,
      availabilityScheduleId: values.availabilityScheduleId,
      enable_currency_conversion: true,
      selected_currencies: values.selected_currencies || [],
    };
    editBundle(payload);
  }

  const isFormError = Object.keys(formMethods.formState.errors).length > 0;
  const isGeneralInformationError =
    formMethods.formState.errors.name ||
    formMethods.formState.errors.description ||
    formMethods.formState.errors.coverImage ||
    formMethods.formState.errors.slug ||
    formMethods.formState.errors.callDuration ||
    formMethods.formState.errors.customCallDuration;
  const isSessionPeriodError =
    formMethods.formState.errors.bookingInterval ||
    formMethods.formState.errors.numberOfSessions;
  const isCoverImagesError = formMethods.formState.errors.cover_images;
  const isPricingError =
    formMethods.formState.errors.callPrice ||
    formMethods.formState.errors.callPriceCurrency ||
    formMethods.formState.errors.callPriceType ||
    formMethods.formState.errors.currencies;
  const isLocationError = formMethods.formState.errors.callLocation;
  const isSettingsError =
    formMethods.formState.errors.hideFromProfile ||
    formMethods.formState.errors.isUrlRedirect ||
    formMethods.formState.errors.redirectAfterPurchase ||
    formMethods.formState.errors.telegramGroupId ||
    formMethods.formState.errors.requestCustomerPhoneNumber ||
    formMethods.formState.errors.url_redirect;
  const isAvailabilityError = formMethods.formState.errors.availabilityScheduleId;

  // console.log({editwatch: formMethods.watch(), editerrors: formMethods.formState.errors});
  return (
    <ServiceFormLayout>
      <FormProvider {...formMethods}>
        <form
          onSubmit={formMethods.handleSubmit(onSubmit, scrollToFirstOfferingFormError)}
          autoComplete="off"
        >
          <Space direction="vertical" size={16} style={{display: "flex"}}>
            <EditBundleSessionOfferingForm
              formType="edit"
              isGeneralInformationError={!!isGeneralInformationError}
              isLocationError={!!isLocationError}
              isPricingError={!!isPricingError}
              isSettingsError={!!isSettingsError}
              isSessionPeriodError={!!isSessionPeriodError}
              isCoverImagesError={!!isCoverImagesError}
              isAvailabilityError={!!isAvailabilityError}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    onClick={() => {
                      navigate("/calls");
                    }}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType={"submit"}
                    size="small"
                    disabled={
                      Object.keys(formMethods.formState.errors).length > 0 ||
                      Object.values(formMethods.watch()).length < 1
                    }
                    loading={isEditingBundle}
                  >
                    Update bundle
                  </Button>
                  <OfferingDeleteButton
                    cancelText="Exit"
                    content="Are you sure you want to delete this bundle? This action cannot be undone."
                    okText="Delete bundle"
                    onOk={deleteBundle}
                    title="Confirm bundle deletion"
                  />
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </ServiceFormLayout>
  );
}
